import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  getCustomInputScopeErrorMessage,
  hasCustomInputScopeError,
} from "@/utils/formErrors";
import SearchableSelector from "@/components/ui/others/SearchableSelector";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/ui/library/multi-select";
import { Lock } from "lucide-react";
import { Controller } from "react-hook-form";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";
import { isTagSelector } from "@/features/customField/utils/cfSelectionMode";

export default function PrefrenceSelectors({
  val,
  index,
  control,
  errors: _errors,
  resolvedOptions,
  autoFill,
  readOnly,
  fullWidth = false,
}: any) {
  // Single vs multi is the FIELD's own setting (Tag / Normal on the custom
  // field form), not the caller's. Callers used to pass `multiple` — the lead
  // panel said true, the deal panel said false — which is how one field ended
  // up with two behaviours in one drawer.
  const multiple = isTagSelector(val);
  // Stage-requirement star (null outside a marks provider → no change).
  const required = useRequiredFieldMark("custom", val?.id);
  const scopeError = _errors?.customInputScopes?.[index];
  const scopeErrMsg = getCustomInputScopeErrorMessage(scopeError);
  const scopeInvalid = hasCustomInputScopeError(scopeError);

  const displayOptions = useMemo(() => {
    if (!resolvedOptions?.length) return val?.options ?? [];
    const resolvedIds = new Set(resolvedOptions.map((o: any) => o.id));
    return (val?.options ?? []).filter((op: any) => resolvedIds.has(op.id));
  }, [val?.options, resolvedOptions]);

  // `{ name, value }` — the shape `SearchableSelector` takes.
  const selectOptions = useMemo(
    () =>
      displayOptions.map((op: any) => ({
        value: String(op.id),
        name: op.value,
      })),
    [displayOptions]
  );

  const autoFillLabel = useMemo(() => {
    if (!autoFill || !resolvedOptions?.length) return null;
    if (resolvedOptions.length === 1) return resolvedOptions[0].value;
    return null;
  }, [autoFill, resolvedOptions]);

  return (
    <div
      key={val.id}
      className={`flex flex-col gap-1.5 ${
        fullWidth ? "col-span-2" : "[&:last-child:nth-child(odd)]:col-span-2"
      }`}
    >
      <Label
        htmlFor={`${val}-${index}`}
        className="text-[#051912] text-sm tracking-[-0.56px] font-normal leading-[1.35] flex items-center gap-1.5"
      >
        {getCfDisplayName(val)}
        <RequiredStar mark={required} className="-ml-1" />
        {autoFill && <Lock className="h-3 w-3 text-slate-400" />}
      </Label>

      {autoFillLabel ? (
        <div className="bg-[#f5f5f5] border border-[#cdd1d0] h-10 rounded-xl px-3 py-2 text-sm w-full flex items-center text-[#051912] opacity-70 cursor-not-allowed">
          {autoFillLabel}
        </div>
      ) : multiple ? (
        <Controller
          name={`customInputScopes.${index}`}
          control={control}
          render={({ field }) => {
            const selected: string[] = Array.isArray(field.value?.optionIds)
              ? field.value.optionIds.map((id: unknown) => String(id))
              : [];
            const triggerOptions = displayOptions.map((op: any) => ({
              value: String(op.id),
              label: op.value,
            }));
            return (
              <MultiSelect
                value={selected}
                onValueChange={(next) =>
                  field.onChange({
                    customInputId: val.id,
                    optionIds: next,
                  })
                }
              >
                <MultiSelectTrigger
                  disabled={readOnly}
                  className={`bg-[#fcfcfc] h-10 rounded-xl px-3 py-2 text-sm w-full ${readOnly ? "opacity-70 cursor-not-allowed" : ""} ${
                    scopeInvalid ? "border border-red-500" : "border border-[#cdd1d0]"
                  }`}
                >
                  <MultiSelectValue
                    placeholder={`Select ${getCfDisplayName(val).toLowerCase()}`}
                    options={triggerOptions}
                  />
                </MultiSelectTrigger>
                <MultiSelectContent
                  searchPlaceholder={`Search ${getCfDisplayName(val).toLowerCase()}…`}
                >
                  {displayOptions.map((op: any) => (
                    <MultiSelectItem key={op.id} value={String(op.id)}>
                      {op.value}
                    </MultiSelectItem>
                  ))}
                </MultiSelectContent>
              </MultiSelect>
            );
          }}
        />
      ) : (
        <Controller
          name={`customInputScopes.${index}`}
          control={control}
          render={({ field }) => {
            const selected = field.value?.optionIds?.[0];
            return (
              <SearchableSelector
                disabled={readOnly}
                // Option order is the tenant's (intake years, score bands,
                // country priority), so it must survive the picker.
                preserveOrder
                options={selectOptions}
                value={selected != null ? String(selected) : ""}
                onChange={(value) =>
                  field.onChange({
                    customInputId: val.id,
                    optionIds: [value],
                  })
                }
                placeholder={`Select ${getCfDisplayName(val).toLowerCase()}`}
                searchInputPlaceholder={`Search ${getCfDisplayName(val).toLowerCase()}…`}
                triggerClassName={`bg-[#fcfcfc] h-10 rounded-xl px-3 py-2 text-sm w-full ${readOnly ? "opacity-70 cursor-not-allowed" : ""} ${
                  scopeInvalid ? "border border-red-500" : "border border-[#cdd1d0]"
                }`}
              />
            );
          }}
        />
      )}
      {scopeErrMsg ? (
        <p className="text-sm text-red-500 mt-1">{scopeErrMsg}</p>
      ) : null}
    </div>
  );
}
